import { Button } from "primereact/button";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { useEffect, useState, useRef } from "react";
import { Toast } from "primereact/toast";
import { POSProductModel } from "../../models/all-user/edit-user-model";
import posService from "../../services/pos/pos.service";
import { Tag } from 'primereact/tag';
import InwardProduct from "./Inward-product";
import OutwardProduct from "./outward-product";
import AdjustProduct from "./Adjust-product";
import RequestProduct from "./request-product";

interface DataTableProps {
  data: POSProductModel[];
  onRefresh: (posId: number[]) => void;
  setLoading: (loading: boolean) => void;
   selectedPosIds?: number[]; // ✅ Add this
}

const PosListDatatable: React.FC<DataTableProps> = ({ data, onRefresh, setLoading , selectedPosIds = [],}) => {
  const toast = useRef<Toast>(null);
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [showInwardDialog, setShowInwardDialog] = useState(false);
  const [showOutwardDialog, setShowOutwardDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);

  const showMessage = (summary: string, detail: string, severity: "success" | "error") => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  // Coerce to non-negative integer; empty/invalid -> 0
  const toNonNegativeInt = (val: any): number => {
    const n = parseInt(String(val), 10);
    return Number.isNaN(n) || n < 0 ? 0 : n;
  };

  useEffect(() => {
    setTableData(data);
  }, [data]);

  // Allow empty string while editing
const safetyEditor = (options: ColumnEditorOptions) => (
  <InputText
    type="text"
    value={options.value ?? ""}
    onChange={(e) => {
      let val = e.target.value;

      // Remove all characters except 0–9 and dot
      val = val.replace(/[^0-9.]/g, "");

      // Allow only one decimal
      const parts = val.split(".");
      if (parts.length > 2) {
        val = parts[0] + "." + parts.slice(1).join("");
      }

      options.editorCallback?.(val);
    }}
    style={{ width: "100%" }}
  />
);


const reorderEditor = (options: ColumnEditorOptions) => (
  <InputText
    type="text"
    value={options.value ?? ""}
    onChange={(e) => {
      let val = e.target.value;

      // Remove all characters except 0–9 and dot
      val = val.replace(/[^0-9.]/g, "");

      // Allow only one decimal
      const parts = val.split(".");
      if (parts.length > 2) {
        val = parts[0] + "." + parts.slice(1).join("");
      }

      options.editorCallback?.(val);
    }}
    style={{ width: "100%" }}
  />
);


  const onRowEditComplete = async (e: DataTableRowEditCompleteEvent) => {
    // Clone and sanitize on commit
    const updatedRow = { ...e.newData };
    updatedRow.safetyStock = toNonNegativeInt(updatedRow.safetyStock);
    updatedRow.reorderStock = toNonNegativeInt(updatedRow.reorderStock);

    try {
      setLoading(true);

      const updatePayload = {
        productId: updatedRow.productId,  // Ensure this field exists in your data
        posId: updatedRow.posId,          // Ensure this field exists in your data
        safetyStock: updatedRow.safetyStock,
        reorderStock: updatedRow.reorderStock,
      };

      const response = await posService.UpdatePosProducts(updatePayload);

      if (response?.isSuccess) {
        const updated = [...tableData];
        const row = { ...updatedRow };

        // Recompute status based on sanitized values
        if (row.currentStock < row.safetyStock) {
          row.status = "Critical";
        } else if (row.currentStock < row.reorderStock) {
          row.status = "Low";
        } else {
          row.status = "Sufficient";
        }

        updated[e.index] = row;
        setTableData(updated);

        showMessage("Success", response.message || "Row updated successfully", "success");
      } else {
        showMessage("Error", response?.message || "Failed to update row", "error");
      }
    } catch (error) {
      console.error("Update error:", error);
      showMessage("Error", "An unexpected error occurred while updating.", "error");
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  };

  const statusBodyTemplate = (rowData: any) => {
    let severity: "danger" | "warning" | "success" | undefined;

    if (rowData.status === "Critical") {
      severity = "danger";
    } else if (rowData.status === "Low") {
      severity = "warning";
    } else if (rowData.status === "Sufficient") {
      severity = "success";
    }

    return <Tag value={rowData.status} severity={severity} />;
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div className="col-12">
        <div className="card border-noround p-2">
          <DataTable
            value={tableData}
            editMode="row"
            onRowEditComplete={onRowEditComplete}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50, 100]}
            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            emptyMessage={
  selectedPosIds.length === 0 ? (
    <div className="text-center py-5 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 shadow-sm">
      <i className="pi pi-info-circle mr-2 text-blue-600 text-lg"></i>
      Please select at least one POS to view the inventory.
    </div>
  ) : data.length === 0 ? (
    <div className="text-center py-5 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 shadow-sm">
      <i className="pi pi-exclamation-triangle mr-2 text-yellow-600 text-lg"></i>
      No products found.
    </div>
  ) : null
}

          >
            <Column field="posCode" header="POS" style={{ width: "15%" }} sortable />
            <Column field="productName" header="Product" style={{ width: "15%" }} sortable />
            <Column field="currentStock" header="Stock" style={{ width: "10%" }} sortable />
            <Column
              field="measurementTypeName"
              header="Measurement Type"
              style={{ width: "15%" }}
              sortable
            />
            <Column
              field="safetyStock"
              header="Safety"
              editor={safetyEditor}
              style={{ width: "10%" }}
              sortable
            />
            <Column
              field="reorderStock"
              header="Reorder"
              editor={reorderEditor}
              style={{ width: "10%" }}
              sortable
            />
            <Column
              field="status"
              header="Status"
              body={statusBodyTemplate}
              style={{ width: "10%" }}
              sortable
            />
            <Column
              header="Action"
              style={{ width: '30%' }}
              body={(rowData) => (
                <div className="flex flex-wrap gap-2 justify-content-center">
                  <Button
                    label="Inward"
                    severity="success"
                    text
                    raised
                    className="px-1 py-2 text-sm"
                    onClick={() => {
                      setSelectedProduct(rowData);
                      setShowInwardDialog(true);
                    }}
                  />
                  <Button
                    label="Outward"
                    severity="warning"
                    text
                    raised
                    className="px-1 py-2 text-sm"
                    onClick={() => {
                      setSelectedProduct(rowData);
                      setShowOutwardDialog(true);
                    }}
                  />
                  <Button
                    label="Adjust"
                    severity="info"
                    text
                    raised
                    className="px-1 py-2 text-sm"
                    onClick={() => {
                      setSelectedProduct(rowData);
                      setShowAdjustDialog(true);
                    }}
                  />
                  <Button
                    label="Request"
                    severity="secondary"
                    text
                    raised
                    className="px-1 py-2 text-sm"
                    onClick={() => {
                      setSelectedProduct(rowData);
                      setShowRequestDialog(true);
                    }}
                  />
                </div>
              )}
            />
            <Column rowEditor header="Edit" bodyStyle={{ textAlign: 'center' }} />
          </DataTable>

          <InwardProduct
            visible={showInwardDialog}
            onHide={() => setShowInwardDialog(false)}
            product={selectedProduct}
            onSuccess={onRefresh}
            setLoading={setLoading}
          />
          <OutwardProduct
            visible={showOutwardDialog}
            onHide={() => setShowOutwardDialog(false)}
            product={selectedProduct}
            onSuccess={onRefresh}
            setLoading={setLoading}
          />
          <AdjustProduct
            visible={showAdjustDialog}
            onHide={() => setShowAdjustDialog(false)}
            product={selectedProduct}
            onSuccess={onRefresh}
            setLoading={setLoading}
          />
          <RequestProduct
            visible={showRequestDialog}
            onHide={() => setShowRequestDialog(false)}
            product={selectedProduct}
            setLoading={setLoading}
          />
        </div>
      </div>
    </>
  );
};

export default PosListDatatable;
 